import { useCallback, useEffect, useMemo, useState } from "react";

import { firmsApi, type Firm, type FirmInput, type FirmPatch, ApiError } from "../lib/api";
import FirmForm from "../components/FirmForm";
import Modal from "../components/Modal";

type Mode =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; firm: Firm };

export default function Firms() {
  const [firms, setFirms] = useState<Firm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: "closed" });
  const [busyName, setBusyName] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      // Always fetch everything so the toggle works without a refetch.
      const items = await firmsApi.list({ includeInactive: true });
      setFirms(items);
    } catch (e) {
      setError(messageFrom(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visible = useMemo(() => {
    if (!firms) return null;
    const filtered = showInactive ? firms : firms.filter((f) => f.active);
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [firms, showInactive]);

  async function handleCreate(input: FirmInput | FirmPatch) {
    await firmsApi.create(input as FirmInput);
    setMode({ kind: "closed" });
    await refresh();
  }

  async function handleUpdate(name: string, patch: FirmInput | FirmPatch) {
    await firmsApi.update(name, patch as FirmPatch);
    setMode({ kind: "closed" });
    await refresh();
  }

  async function handleDeactivate(name: string) {
    if (!confirm(`Deactivate "${name}"? Existing accounts that reference it keep working; the firm just disappears from the new-account dropdown. You can reactivate any time.`)) return;
    setBusyName(name);
    try {
      await firmsApi.deactivate(name);
      await refresh();
    } catch (e) {
      setError(messageFrom(e));
    } finally {
      setBusyName(null);
    }
  }

  async function handleReactivate(firm: Firm) {
    setBusyName(firm.name);
    try {
      await firmsApi.update(firm.name, { active: true });
      await refresh();
    } catch (e) {
      setError(messageFrom(e));
    } finally {
      setBusyName(null);
    }
  }

  return (
    <section className="accounts">
      <header className="accounts-header">
        <h1>Prop firms</h1>
        <div className="header-actions">
          <label className="inline-checkbox">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <button className="primary" onClick={() => setMode({ kind: "create" })}>
            + New firm
          </button>
        </div>
      </header>

      {error && <div className="error">{error}</div>}

      {visible === null ? (
        <p>Loading&hellip;</p>
      ) : visible.length === 0 ? (
        <p className="empty">
          No firms{showInactive ? "" : " active"}. Click <strong>+ New firm</strong> to add one
          {showInactive ? "" : ", or toggle Show inactive to see deactivated firms"}.
        </p>
      ) : (
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Website</th>
              <th>TradeLocker</th>
              <th>Active</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((f) => (
              <tr key={f.name} className={f.active ? "" : "row-inactive"}>
                <td>{f.name}</td>
                <td>
                  {f.website ? (
                    <a href={f.website} target="_blank" rel="noopener noreferrer">
                      {prettyUrl(f.website)}
                    </a>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{f.supports_tradelocker ? "Yes" : "No"}</td>
                <td>{f.active ? "Yes" : "No"}</td>
                <td className="notes-cell">
                  {f.notes ? f.notes : <span className="muted">—</span>}
                </td>
                <td className="row-actions">
                  <button
                    className="link-button"
                    onClick={() => setMode({ kind: "edit", firm: f })}
                  >
                    Edit
                  </button>
                  {f.active ? (
                    <button
                      className="link-button danger"
                      disabled={busyName === f.name}
                      onClick={() => handleDeactivate(f.name)}
                    >
                      {busyName === f.name ? "…" : "Deactivate"}
                    </button>
                  ) : (
                    <button
                      className="link-button"
                      disabled={busyName === f.name}
                      onClick={() => handleReactivate(f)}
                    >
                      {busyName === f.name ? "…" : "Reactivate"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal open={mode.kind !== "closed"} onClose={() => setMode({ kind: "closed" })}>
        {mode.kind === "create" && (
          <FirmForm
            title="New firm"
            onCancel={() => setMode({ kind: "closed" })}
            onSubmit={handleCreate}
          />
        )}
        {mode.kind === "edit" && (
          <FirmForm
            title={`Edit ${mode.firm.name}`}
            initial={mode.firm}
            onCancel={() => setMode({ kind: "closed" })}
            onSubmit={(patch) => handleUpdate(mode.firm.name, patch)}
          />
        )}
      </Modal>
    </section>
  );
}

function prettyUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.host + (u.pathname && u.pathname !== "/" ? u.pathname : "");
  } catch {
    return url;
  }
}

function messageFrom(e: unknown): string {
  if (e instanceof ApiError) return `${e.message} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return String(e);
}
